import { useMemo } from 'react';
import type { ColDef, ICellRendererParams } from 'ag-grid-community';
import {
    NJButton,
    NJIconButton,
    NJInlineMessage,
    NJText,
    NJToastService,
} from '@engie-group/fluid-design-system-react';
import DataTable from '@shared/DataTable';
import {
    Size2MixAccordion,
    Size2MixExpandCollapseBar,
    Size2MixSection,
    useSectionsToggle,
} from '@shared/size2mix-shell';
import { useEnergyContracts } from './hooks';
import type { PowerEnergyContract, WaterEnergyContract } from './types';
import './energy-contracts.css';

const SECTIONS = ['electricity', 'water'] as const;
const DEFAULT_COLUMN: ColDef = {
    flex: 1,
    resizable: true,
    sortable: true,
    wrapHeaderText: true,
    autoHeaderHeight: true,
};

function VariablePriceAction() {
    return (
        <div className="energy-contracts-cell-center">
            <NJIconButton
                icon="open_in_new"
                label="Prix variable"
                variant="tertiary"
                scale="sm"
                isDisabled
            />
        </div>
    );
}

function ContractActions({ name, canWrite }: { name: string; canWrite: boolean }) {
    if (!canWrite) return null;
    return (
        <div className="energy-contracts-actions">
            <NJIconButton
                icon="edit"
                label={`Modifier ${name}`}
                variant="tertiary"
                scale="sm"
                onClick={() =>
                    NJToastService(`Modification de « ${name} »`, {
                        iconName: 'info',
                        dismissAfter: 2500,
                    })
                }
            />
            <NJIconButton
                icon="delete"
                label={`Supprimer ${name}`}
                variant="tertiary"
                scale="sm"
                onClick={() =>
                    NJToastService(`Suppression de « ${name} » non exécutée dans la référence`, {
                        iconName: 'info',
                        dismissAfter: 3000,
                    })
                }
            />
        </div>
    );
}

export function EnergyContractsTab({ canWrite }: { canWrite: boolean }) {
    const { bind, expandAll, collapseAll } = useSectionsToggle(SECTIONS, false);
    const { data, isLoading, isError, error } = useEnergyContracts();

    const powerColumns = useMemo<ColDef<PowerEnergyContract>[]>(
        () => [
            { field: 'name', headerName: 'Nom', filter: true, flex: 3, cellClass: 'energy-contracts-readonly' },
            { field: 'linkedSites', headerName: 'Sites liés', cellClass: 'energy-contracts-readonly energy-contracts-center' },
            { field: 'linkedProductionUnits', headerName: 'Équipements de production liés', flex: 2, cellClass: 'energy-contracts-readonly energy-contracts-center' },
            { field: 'linkedPumpGroups', headerName: 'Groupes de pompes liés', flex: 2, cellClass: 'energy-contracts-readonly energy-contracts-center' },
            { field: 'fixedPrice', headerName: 'Prix fixe (€/J)', cellClass: 'energy-contracts-center' },
            { headerName: 'Prix variable (€/J)', sortable: false, cellRenderer: VariablePriceAction },
            { field: 'description', headerName: 'Description', flex: 2 },
            {
                headerName: 'Actions',
                sortable: false,
                cellRenderer: ({ data: row }: ICellRendererParams<PowerEnergyContract>) =>
                    row ? <ContractActions name={row.name} canWrite={canWrite} /> : null,
            },
        ],
        [canWrite],
    );

    const waterColumns = useMemo<ColDef<WaterEnergyContract>[]>(
        () => [
            { field: 'name', headerName: 'Nom', filter: true, flex: 3, cellClass: 'energy-contracts-readonly' },
            { field: 'linkedSites', headerName: 'Sites liés', cellClass: 'energy-contracts-readonly energy-contracts-center' },
            { field: 'linkedCoolingSystems', headerName: 'Systèmes de refroidissement liés', flex: 2, cellClass: 'energy-contracts-readonly energy-contracts-center' },
            { field: 'fixedPrice', headerName: 'Prix fixe (€/J)', cellClass: 'energy-contracts-center' },
            { headerName: 'Prix variable (€/J)', sortable: false, cellRenderer: VariablePriceAction },
            { field: 'description', headerName: 'Description', flex: 2 },
            {
                headerName: 'Actions',
                sortable: false,
                cellRenderer: ({ data: row }: ICellRendererParams<WaterEnergyContract>) =>
                    row ? <ContractActions name={row.name} canWrite={canWrite} /> : null,
            },
        ],
        [canWrite],
    );

    if (isLoading) {
        return <NJInlineMessage variant="information">Chargement des contrats d’énergies…</NJInlineMessage>;
    }
    if (isError || !data) {
        return (
            <NJInlineMessage variant="error" title="Chargement impossible">
                {error instanceof Error ? error.message : 'Les contrats ne sont pas disponibles.'}
            </NJInlineMessage>
        );
    }

    const create = (kind: string) =>
        NJToastService(`Création d’un contrat ${kind}`, {
            iconName: 'info',
            dismissAfter: 2500,
        });

    return (
        <div className="energy-contracts">
            <Size2MixAccordion>
                <Size2MixExpandCollapseBar onExpandAll={expandAll} onCollapseAll={collapseAll} />
                <Size2MixSection
                    togglePosition="trailing"
                    title="Électricité"
                    icon="bolt"
                    {...bind('electricity')}
                >
                    <div className="energy-contracts-body">
                        {canWrite && <NJButton label="Créer un nouveau contrat" icon="add" onClick={() => create('d’électricité')} />}
                        <NJText as="p" scale="sm" style={{ fontWeight: 700 }}>Contrats d’électricité</NJText>
                        <DataTable<PowerEnergyContract>
                            testId="power-energy-contracts"
                            header={powerColumns}
                            data={data.powerContracts}
                            defaultColDef={DEFAULT_COLUMN}
                            isPaginated
                            paginationPageSize={15}
                        />
                    </div>
                </Size2MixSection>
                <Size2MixSection
                    togglePosition="trailing"
                    title="Eau"
                    icon="water_drop"
                    {...bind('water')}
                >
                    <div className="energy-contracts-body">
                        {canWrite && <NJButton label="Créer un nouveau contrat" icon="add" onClick={() => create('d’eau')} />}
                        <NJText as="p" scale="sm" style={{ fontWeight: 700 }}>Contrats d’eau</NJText>
                        <DataTable<WaterEnergyContract>
                            testId="water-energy-contracts"
                            header={waterColumns}
                            data={data.waterContracts}
                            defaultColDef={DEFAULT_COLUMN}
                            isPaginated
                            paginationPageSize={15}
                        />
                    </div>
                </Size2MixSection>
            </Size2MixAccordion>
        </div>
    );
}
